import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { Dot } from "../Dot";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { useLogoRepository } from "../../../data/repositories/LogoRepository";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { useSuccessToast } from "../Toast/Toast";

interface LogoStorageProps {
  logo: BaseLogo;
  showRefreshButton?: boolean;
}

export function LogoStorage({
  logo,
  showRefreshButton = false,
}: LogoStorageProps) {
  const isNumeric = logo.logoType === "NumberLogo";
  if (isNumeric)
    return (
      <NumberLogoStorage logo={logo} showRefreshButton={showRefreshButton} />
    );
  else
    return (
      <SinglePointLogoStorage
        logo={logo}
        showRefreshButton={showRefreshButton}
      />
    );
}

// Helper function to ensure all 11 numberset items are present
function getCompleteNumbersetStorage(
  numberLogosInStorage: { value: string; inStorage: number }[]
) {
  // Define all 11 items that should be present in a numberset
  const allNumbersetItems = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

  return allNumbersetItems.map((value) => {
    const existing = numberLogosInStorage.find((item) => item.value === value);
    return {
      value,
      inStorage: existing?.inStorage ?? 0,
    };
  });
}

export const NumberLogoStorage = ({
  logo,
  showRefreshButton = false,
}: LogoStorageProps) => {
  const completeStorage = getCompleteNumbersetStorage(
    logo.numberLogosInStorage
  );

  return (
    <div className={"pr-1.5 flex flex-col gap-y-1 font-semibold"}>
      <div className="flex flex-row">
        <div className="block pt-1 text-xs mb-2 w-[80px]">
          <p className="inline uppercase pr-1">Number</p>
        </div>
        <div className="block pt-1 text-xs mb-2">
          <p className="inline uppercase pr-1">In Storage</p>
        </div>
      </div>

      {completeStorage.map((storage) => (
        <div key={storage.value} className="flex flex-row">
          <div className="flex flex-row w-[80px]">
            <div>{storage.value}</div>
          </div>
          <div className="flex flex-row items-center">
            <Dot
              className={`${
                Math.max(0, storage.inStorage ?? 0) > 0
                  ? "!bg-status-success"
                  : ""
              }`}
            />
            <div>{Math.max(0, storage.inStorage ?? 0)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const SinglePointLogoStorage = ({
  logo,
  showRefreshButton = false,
}: LogoStorageProps) => {
  const displayStorage = Math.max(0, logo.inStorage ?? 0);
  const { profile } = useProfileContext();
  const { refreshStorageCount } = useLogoRepository();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isAdmin = profile?.userOrganizationInformations.role === Role.Admin;
  const shouldShowRefreshButton = showRefreshButton && isAdmin;

  const handleRefresh = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      await refreshStorageCount(logo.id);
      useSuccessToast(
        "Storage count refresh has been queued. If the logo has an amount in storage, it should update soon."
      );
    } catch (error) {
      console.error("Failed to refresh storage count:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className={"pr-1.5 flex space-x-1 items-center font-semibold"}>
      <Dot className={`${displayStorage > 0 ? "!bg-status-success" : ""}`} />
      {displayStorage}
      {shouldShowRefreshButton && (
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className={`ml-1 p-1 rounded hover:bg-gray-100 transition-colors ${
            isRefreshing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
          title="Refresh storage count"
        >
          <FontAwesomeIcon
            icon={faArrowsRotate}
            className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`}
          />
        </button>
      )}
    </div>
  );
};
